import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import PairTokenIcon from "../../components/PairTokenIcon";
import Colors from "../../styles/Colors";
import { H4 } from "../../styles/Fonts";
import { getTruncNum } from "../../utils/common";
import { ALL_PAIRS, PairProps } from "../../utils/constant/pairs";

const ClosedPosition: React.FC<{ pos: { [key: string]: any } }> = ({ pos }) => {
  const {
    CloseEvent: eventName,
    AmountAfterRepaid: closeAmount,
    WantAmount: startAmount,
  } = pos;

  const { ContractAddr: contractAddress } = pos.DiamondFactoryV3;
  const { WantTokenFee: wantTokenFee, BorrowTokenFee: borrowTokenFee } =
    pos.PositionInfo;

  const [pair, setPair] = useState<PairProps>();

  const pairs = Object.values(ALL_PAIRS).flat();

  const getFactoryPair = useCallback(() => {
    const pair = pairs.find(
      (item) =>
        item.factoryAddress.toUpperCase() === contractAddress.toUpperCase()
    );
    if (pair) {
      setPair(pair);
    }
  }, [contractAddress, pairs]);

  useEffect(() => {
    getFactoryPair();
  }, [getFactoryPair]);

  return (
    <ClosePositionRow key={pos.positionId}>
      <PositionDetail>
        <PositionHeaderM>Pool</PositionHeaderM>
        <Row>
          {pair?.borrowToken.tokenIcon && pair?.wantToken.tokenIcon && (
            <PairTokenIcon
              borrowTokenIcon={pair?.borrowToken.tokenIcon}
              wantTokenIcon={pair?.wantToken.tokenIcon}
            />
          )}
          <PositionValue>{pair?.label}</PositionValue>
        </Row>
      </PositionDetail>
      <PositionDetail>
        <PositionHeaderM>Open Position Value</PositionHeaderM>
        <PositionValue>
          {`${
            pair?.wantToken.tokenDecimal &&
            getTruncNum(+startAmount / 10 ** pair?.wantToken.tokenDecimal, 6)
          } ${pair?.wantToken.tokenSymbol}`}
        </PositionValue>
      </PositionDetail>
      <PositionDetail>
        <PositionHeaderM>Closed Position Value</PositionHeaderM>
        <PositionValue>{`${
          pair?.wantToken.tokenDecimal &&
          getTruncNum(+closeAmount / 10 ** pair?.wantToken.tokenDecimal, 6)
        } ${pair?.wantToken.tokenSymbol}`}</PositionValue>
      </PositionDetail>
      <PositionDetail>
        <PositionHeaderM>Earned Fee</PositionHeaderM>
        <FeeContainer>
          <PositionValueSmall>
            {pair?.wantToken
              ? `${getTruncNum(
                  wantTokenFee / 10 ** pair?.wantToken.tokenDecimal,
                  6
                )} ${pair?.wantToken.tokenSymbol}`
              : "-"}
          </PositionValueSmall>
          <PositionValueSmall>
            {pair?.borrowToken
              ? `${getTruncNum(
                  borrowTokenFee / 10 ** pair?.borrowToken.tokenDecimal,
                  6
                )} ${pair?.borrowToken.tokenSymbol}`
              : "-"}
          </PositionValueSmall>
        </FeeContainer>
      </PositionDetail>
      <PositionDetail>
        <PositionHeaderM>Type</PositionHeaderM>
        <PositionValue>{eventName}</PositionValue>
      </PositionDetail>
    </ClosePositionRow>
  );
};

export default ClosedPosition;

const PositionRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr) 80px;
  align-items: center;
  background-color: ${Colors.backgroundGray4};
  border-radius: 8px;
  padding: 20px;
  margin-top: 12px;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
  }
  div:not(:first-child) {
    justify-content: flex-end;
    text-align: right;
    @media (max-width: 768px) {
      justify-content: space-between;
    }
  }
  h4:not(:first-child) {
    text-align: right;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

const PositionHeader = styled(H4)`
  color: ${Colors.gray4};
  font-size: 14px;
  line-height: 22px;
  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 22px;
  }
  @media (max-width: 576px) {
    font-size: 12px;
    line-height: 20px;
  }
`;

const PositionValue = styled(H4)`
  color: ${Colors.gray1};
  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 22px;
  }
  @media (max-width: 576px) {
    font-size: 12px;
    line-height: 20px;
  }
`;

const PositionValueSmall = styled(H4)`
  color: ${Colors.gray1};
  font-size: 14px;
  line-height: 22px;
  @media (max-width: 576px) {
    font-size: 12px;
    line-height: 20px;
  }
`;

const ClosePositionRow = styled(PositionRow)`
  grid-template-columns: repeat(5, 1fr);
`;

const PositionDetail = styled.div`
  display: flex;
  justify-content: space-between;
  @media (max-width: 768px) {
    width: 100%;
    padding: 4px 0;
  }
`;

const PositionHeaderM = styled(PositionHeader)`
  display: none;
  @media (max-width: 768px) {
    display: block;
  }
`;

const FeeContainer = styled.div`
  @media (max-width: 768px) {
    text-align: right;
  }
`;
